/**
 * Template Field Mapping DTO (Issue #1531, Phase 2 PR B)
 *
 * Binds a DocuSeal-rendered field on a `TermSheetTemplate` to a canonical
 * loan-data-point key from `1-shared-types/src/config/loan-data-points.ts`.
 * This is the structured replacement for the term-sheet "Configure" prefill
 * mapper that was removed in #1430 D4.
 *
 * READ THIS BEFORE TOUCHING THE BINDING SHAPE — REVIEWERS WILL PATTERN-MATCH
 * THIS AS A #1430 D4 REVERSAL OTHERWISE:
 *
 *   #1430 D4 (REMOVED)              vs.   TemplateFieldMapping (THIS)
 *   ─────────────────────────────         ─────────────────────────────
 *   Free-text label aliases               Structured canonical-key binding
 *   "Borrower Email" ↔ "case.borrower.email"
 *                                         DocuSeal field UUID ↔ `borrower_name`
 *                                         (LOAN_DATA_POINTS[].canonicalKey)
 *
 *   Lender re-types both sides            Vocabulary is fixed at the registry
 *   (redundant with DocuSeal's            (~36 keys, validated by Zod refine
 *   own field labels)                     against VALID_CANONICAL_KEYS)
 *
 *   No machine validation — typos         canonicalKey rejected at the tRPC
 *   silently break prefill                boundary if not in the registry
 *
 *   Not LLM-assistable                    LLM suggestions are *constrained*
 *                                         to the registry (no hallucination
 *                                         space — see Phase 3)
 *
 * The mapper is dead. Long live the canonical-key binding.
 *
 * See:
 *   - docs/plans/20260429-PLAN-termsheet-comprehensive-redesign.md §6.4
 *   - docs/plans/20260429-PLAN-termsheet-phase-2-blueprint.md (Workstream B,
 *     "TemplateFieldMapping Prisma Model Spec" section)
 *   - 1-shared-types/src/config/loan-data-points.ts (the binding vocabulary)
 */

import { z } from 'zod';
import { LOAN_DATA_POINTS } from '../config/loan-data-points';
import { PARTNERSHIP_DATA_POINTS } from '../config/partnership-data-points';
import { TERMSHEET_DATA_POINTS } from '../config/termsheet-data-points';

// ============================================================================
// VALUE TRANSFORMS
// ============================================================================

/**
 * Discriminated union for value transforms applied at submission/render time.
 *
 * Mirrors the Handlebars helper allowlist in
 * `term-sheet-context.dto.ts::TERM_SHEET_HELPER_NAMES` so the same vocabulary
 * applies whether a value is rendered server-side (Handlebars) or injected
 * into a DocuSeal `default_value` at submission time.
 *
 * - `currency` — format a number as currency (locale + ISO currency code)
 * - `percent`  — format a number as a percentage (configurable decimals)
 * - `date`     — format an ISO date string (configurable strftime-style format)
 * - `defaultValue` — fixed string injected when the resolved value is missing
 */
export const TemplateFieldTransformSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('currency'),
    args: z
      .object({
        locale: z.string().optional(),
        currency: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('percent'),
    args: z
      .object({
        decimals: z.number().int().min(0).max(6).optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('date'),
    args: z
      .object({
        format: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('defaultValue'),
    args: z.object({
      value: z.string(),
    }),
  }),
]);

export type TemplateFieldTransform = z.infer<typeof TemplateFieldTransformSchema>;

// ============================================================================
// CANONICAL-KEY VOCABULARY
// ============================================================================

/**
 * The full set of valid canonical keys, derived from the registry.
 * Used to refine() Zod inputs at the tRPC boundary so unknown keys
 * are rejected before they reach the database.
 *
 * IMPORTANT: this is a READ-ONLY snapshot of LOAN_DATA_POINTS at module
 * load time. New keys added to the registry are picked up automatically
 * on next deploy (no migration required — the binding is per-row, not
 * per-schema).
 */
export const VALID_CANONICAL_KEYS: readonly string[] = [
  ...LOAN_DATA_POINTS.map((dp) => dp.canonicalKey),
  // Phase 7 follow-up (#1531 v4 §6.4) — partnership-style CONTRACT
  // templates (referral broker, correspondent lending, wholesale, channel
  // partner) bind to the partnership canonical-key registry instead of the
  // loan registry. The Zod refinement at every mutation entry-point unions
  // all vocabularies; the editor's ContextRail picks one source per
  // template kind/category, so a key is never offered by the UI for a
  // template it doesn't apply to. Cross-vocabulary typos (e.g. binding
  // `loan_amount` to a partnership template) are still caught at the field
  // resolution layer at submission time.
  ...PARTNERSHIP_DATA_POINTS.map((dp) => dp.canonicalKey),
  // PR #1624 review (#3198150185) — termsheet canonical-key registry. The
  // FieldMappingRow editor surfaces TERMSHEET_DATA_POINTS keys when
  // sourceKind === 'termsheet'; without the registry here, every save
  // would 400 with "Invalid canonical key". Same union pattern as
  // partnership: the editor narrows by template kind, the resolver in
  // term-sheet-generation.service.ts narrows again by guarding against
  // TERMSHEET_CANONICAL_KEYS before invoking the resolver (defensive
  // double-check against legacy LOAN-key drift in stored mappings).
  ...TERMSHEET_DATA_POINTS.map((dp) => dp.canonicalKey),
];

// ============================================================================
// TEMPLATE FIELD MAPPING
// ============================================================================

/**
 * A single field-to-canonical-key binding for a term-sheet template.
 *
 * One row per (template × DocuSeal field UUID). The `canonicalKey` MUST exist
 * in `LOAN_DATA_POINTS` — the Zod refine below enforces this at every entry
 * point (gRPC controller, tRPC router, direct DTO use).
 *
 * Shape mirrors the Prisma model spec in the Phase 2 blueprint exactly so
 * service-side serialization is a `prisma.findMany() → z.parse()` pass-through.
 */
export const TemplateFieldMappingSchema = z.object({
  id: z.string().uuid(),
  /** Tenant scope — every row is per-business per CLAUDE.md §2 / §2f. */
  businessId: z.string(),
  /** FK → TermSheetTemplate.id (Phase 0 model, not DocumentTemplate). */
  templateId: z.string().uuid(),
  /** The DocuSeal-assigned UUID of the placed field on the rendered template. */
  docuSealFieldUuid: z.string(),
  /**
   * Canonical key from LOAN_DATA_POINTS. Refined against the live registry
   * snapshot — unknown keys are rejected before write.
   */
  canonicalKey: z.string().refine((k) => VALID_CANONICAL_KEYS.includes(k), {
    message: 'canonicalKey must exist in LOAN_DATA_POINTS (1-shared-types/src/config/loan-data-points.ts)',
  }),
  /**
   * Optional value transform applied at submission/render time.
   * `null` and `undefined` both mean "no transform" — pass the resolved value
   * through unchanged.
   */
  transform: TemplateFieldTransformSchema.nullable().optional(),
  /** Whether the field MUST resolve to a value at submission time. */
  isRequired: z.boolean(),
  /**
   * Fallback string used when the resolved canonical-key value is missing.
   * Distinct from `transform: { type: 'defaultValue', ... }` — this default
   * applies even when no transform is configured.
   */
  defaultValue: z.string().nullable().optional(),
  /** ISO 8601 timestamps mirrored from the DB row. */
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type TemplateFieldMappingDto = z.infer<typeof TemplateFieldMappingSchema>;
